import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'

import {
    BaseComponent,
    ForemanSegmentSpan,
    IsoDate,
    JobPhaseOption,
    describeSegmentOverlaps,
    findSegmentOverlaps,
} from '../../shared'

export interface JobAssignmentModalForemanOption {
    hrRef: number
    name: string
    jobTitle: string
    segments: ForemanSegmentSpan[]
}

export interface JobAssignmentModalContext {
    vpJobId: string
    jobLabel: string
    foremen: JobAssignmentModalForemanOption[]
    defaultStartDate: IsoDate
    includeAllPhases: boolean
}

export interface JobAssignmentModalResult {
    foremanHrRef: number
    startDate: IsoDate
    phase: string
    forcedDays: number | null
}

@Component({
    selector: 'app-foreman-schedule-job-assignment-modal',
    templateUrl: './foreman-schedule-job-assignment-modal.component.html',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule],
})
export class ForemanScheduleJobAssignmentModalComponent extends BaseComponent {
    private readonly destroyRef = inject(DestroyRef)
    readonly activeModal = inject(NgbActiveModal)

    context!: JobAssignmentModalContext

    readonly foremanHrRef = signal(0)
    readonly startDate = signal<IsoDate>('')
    readonly phase = signal('')
    readonly phases = signal<JobPhaseOption[]>([])
    readonly loadingPhases = signal(false)
    readonly forcedDays = signal<number | null>(null)
    readonly error = signal<string | null>(null)

    readonly overlapWarning = computed(() => {
        const ctx = this.context
        if (!ctx) return null
        const foreman = ctx.foremen.find(f => f.hrRef === this.foremanHrRef())
        const start = this.startDate()
        if (!foreman || !start) return null
        return describeSegmentOverlaps(
            findSegmentOverlaps(foreman.segments, ctx.vpJobId, this.phase(), start),
            iso => this.formatShortDate(iso),
        )
    })

    initFromContext(context: JobAssignmentModalContext) {
        this.context = context
        this.startDate.set(context.defaultStartDate)
        const firstForeman = (context.foremen ?? [])[0]
        if (firstForeman) this.foremanHrRef.set(firstForeman.hrRef)
        this.loadPhases(context.vpJobId, context.includeAllPhases)
    }

    phaseLabel(option: JobPhaseOption): string {
        const desc = (option.description ?? '').trim()
        return desc && desc !== 'Unknown Phase' ? `${option.phase} — ${desc}` : option.phase
    }

    save() {
        this.error.set(null)
        const foremanHrRef = this.foremanHrRef()
        const startDate = this.startDate()
        const phase = (this.phase() ?? '').trim()

        if (!foremanHrRef) {
            this.error.set('Select a foreman.')
            return
        }
        if (!startDate) {
            this.error.set('Start date is required.')
            return
        }
        if (!phase) {
            this.error.set('Select a phase.')
            return
        }

        this.activeModal.close({
            foremanHrRef,
            startDate,
            phase,
            forcedDays: this.parseForcedDays(this.forcedDays()),
        } satisfies JobAssignmentModalResult)
    }

    cancel() {
        this.activeModal.dismiss()
    }

    private loadPhases(vpJobId: string, includeAllPhases: boolean) {
        this.loadingPhases.set(true)
        this.phases.set([])
        this.phase.set('')

        this.services.data
            .post<JobPhaseOption[]>('api/ForemanSchedule/GetPhasesForJob', {
                vpJobId,
                includeAllPhases,
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: phases => {
                    this.phases.set((phases ?? []).map(p => ({ ...p, remainingHours: p.remainingHours ?? 0 })))
                    this.loadingPhases.set(false)
                },
                error: () => {
                    this.phases.set([])
                    this.loadingPhases.set(false)
                    this.error.set('Failed to load phases for this job.')
                },
            })
    }

    private parseForcedDays(value: number | string | null | undefined): number | null {
        if (value == null || value === '') return null
        const n = Math.floor(Number(value))
        return Number.isFinite(n) && n > 0 ? n : null
    }

    private formatShortDate(iso: IsoDate): string {
        const [y, m, d] = iso.split('-').map(n => parseInt(n, 10))
        const dt = new Date(Date.UTC(y, m - 1, d))
        return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })
    }
}
